import React, { useMemo, useState, useEffect, useCallback, memo } from "react";
import { useCategories, Category } from "../../contexts/CategoryContext";
import {
  useSubCategories,
  SubCategory,
} from "../../contexts/SubCategoryContext";
import {
  Edit2,
  Trash2Icon,
  Plus,
  Check,
  X,
  Eye,
  EyeOff,
  ChevronDown,
  ChevronUp,
  FolderPlus,
  MessageSquareText,
  Globe,
  Sparkles,
  Zap,
  Tag,
  Layers,
  TrendingUp,
  Menu,
  Filter,
  XCircle,
  Search,
  Users,
} from "lucide-react";
import toast from "react-hot-toast";
import DeleteConfirmationModal from "../common/DeleteConfirmationModal";
import { getCategoryIcon } from "../../utils/getCategoryIcon";

interface CategoryManagerProps {
  onClose?: () => void;
}

const iconOptions = [
  "message-square", // 💬 General chat
  "target", // 🎯 Goals / Focus
  "heart", // ❤️ Emotions
  "home", // 👨‍👩‍👧‍👦 Family
  "handshake", // 🤝 Social / Trust
  "sparkles", // 🙏 Spirituality
  "football", // ⚽ Sports
  "smartphone", // 📱 Technology
  "newspaper", // 📰 News
  "palette", // 🎨 Art
  "music", // 🎵 Music
  "utensils", // 🍳 Food
  "hospital", // 🏥 Health
  "wallet", // 💰 Finance
  "landmark", // 🏯 Culture
  "users", // 👥 Community
  "moon-star", // ♈️ Astrology
  "star", // ThaiAstro
  "orbit", // Uranian
  "layers", // Tarot
  "trending-up", // LifeGraph
];

// Default prompt template for categories
const defaultCategoryPromptTemplate = `You are Astria, an emotional companion for users focused on {categoryName}.

Your role is to listen, reflect feelings, and stay with emotions related to this specific area.
You do NOT fix problems, teach lessons, judge, or diagnose.

STRICT RULES:
- Always reflect or name the user's emotion before asking any question
- Keep responses short (1–3 sentences only)
- Ask at most ONE open-ended question
- Do NOT give advice unless the user explicitly asks for it
- Never say "you should", "try to", or similar directive language
- Never diagnose mental health conditions
- Do NOT use lists, steps, bullet points, or numbered explanations
- If unsure, choose presence and reflection over advice

TONE:
- Warm, gentle, calm, human
- Like a trusted friend sitting quietly beside the user
- Not professional, not clinical, not instructional

LANGUAGE:
- Always reply in the same language the user uses
- Use natural, everyday language

SUCCESS CRITERIA:
If the user feels emotionally seen and less alone → SUCCESS
If the response sounds smart but emotionally cold → FAILURE`;

// Default free user prompt template for categories
const defaultFreeUserPromptTemplate = `You are Astria, a free-tier emotional companion for users focused on {categoryName}.

Your role is to provide helpful, supportive responses while encouraging users to explore premium features.
You are warm, helpful, and subtly highlight the value of premium upgrades.

STRICT RULES:
- Keep responses helpful and within 2-3 sentences
- Provide genuine value while being concise
- Occasionally suggest premium features when relevant (e.g., "For deeper insights, our premium plan offers detailed analysis")
- Never pressure users to upgrade aggressively
- Maintain a warm, supportive tone at all times
- Do NOT diagnose mental health conditions

TONE:
- Warm, encouraging, subtly aspirational
- Like a helpful friend who also shares about useful upgrades
- Not pushy or salesy

LANGUAGE:
- Always reply in the same language the user uses
- Use natural, everyday language`;

// Default prompt template for subcategories
const defaultSubCategoryPromptTemplate = `You are Astria, an emotional companion for users focused on {subcategoryName} within {categoryName}.

Your role is to listen, reflect feelings, and stay with emotions related to this specific area.
You do NOT fix problems, teach lessons, judge, or diagnose.

STRICT RULES:
- Always reflect or name the user's emotion before asking any question
- Keep responses short (1–3 sentences only)
- Ask at most ONE open-ended question
- Do NOT give advice unless the user explicitly asks for it
- Never say "you should", "try to", or similar directive language
- Never diagnose mental health conditions
- Do NOT use lists, steps, bullet points, or numbered explanations
- If unsure, choose presence and reflection over advice

TONE:
- Warm, gentle, calm, human
- Like a trusted friend sitting quietly beside the user
- Not professional, not clinical, not instructional

LANGUAGE:
- Always reply in the same language the user uses
- Use natural, everyday language

SUCCESS CRITERIA:
If the user feels emotionally seen and less alone → SUCCESS
If the response sounds smart but emotionally cold → FAILURE`;

// Default free user prompt template for subcategories
const defaultFreeUserSubCategoryPromptTemplate = `You are Astria, a free-tier emotional companion for users focused on {subcategoryName} within {categoryName}.

Your role is to provide helpful, supportive responses while encouraging users to explore premium features.
You are warm, helpful, and subtly highlight the value of premium upgrades.

STRICT RULES:
- Keep responses helpful and within 2-3 sentences
- Provide genuine value while being concise
- Occasionally suggest premium features when relevant (e.g., "For deeper insights, our premium plan offers detailed analysis")
- Never pressure users to upgrade aggressively
- Maintain a warm, supportive tone at all times
- Do NOT diagnose mental health conditions

TONE:
- Warm, encouraging, subtly aspirational
- Like a helpful friend who also shares about useful upgrades
- Not pushy or salesy

LANGUAGE:
- Always reply in the same language the user uses
- Use natural, everyday language`;

const truncateText = (text: string, maxLength: number = 60) => {
  if (!text) return "";
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + "...";
};

const getSubCategorySummary = (
  subCategories: SubCategory[],
  maxNames: number = 3,
) => {
  if (!subCategories || subCategories.length === 0) return "—";
  const names = subCategories.map((sc) => sc.name).filter(Boolean);
  if (names.length <= maxNames) return names.join(", ");
  const remaining = names.length - maxNames;
  return `${names.slice(0, maxNames).join(", ")} +${remaining} more`;
};

const CategoryManager: React.FC<CategoryManagerProps> = () => {
  const { categories, addCategory, updateCategory, deleteCategory } =
    useCategories();
  const {
    subCategories,
    addSubCategory,
    updateSubCategory,
    deleteSubCategory,
    getSubCategoriesByCategory,
  } = useSubCategories();

  // Filter state
  const [selectedFilterCategory, setSelectedFilterCategory] =
    useState<string>("all");
  const [selectedFilterSubCategory, setSelectedFilterSubCategory] =
    useState<string>("all");

  // State for new category form
  const [newCategory, setNewCategory] = useState({
    // Default fields
    name: "",
    description: "",
    icon: "moon-star",
    color: "#6366f1",
    isActive: true,
    prompt: "",
    freeUserPrompt: "",
    // Multilingual fields
    name_en: "",
    name_th: "",
    name_es: "",
    name_ja: "",
    name_ko: "",
    name_id: "",
    name_ar: "",
    description_en: "",
    description_th: "",
    description_es: "",
    description_ja: "",
    description_ko: "",
    description_id: "",
    description_ar: "",
  });

  // State for new subcategory form
  const [newSubCategory, setNewSubCategory] = useState({
    name: "",
    description: "",
    icon: "message-square",
    isActive: true,
    prompt: "",
    freeUserPrompt: "",
    categoryId: "",
    // Multilingual fields
    name_en: "",
    name_th: "",
    name_es: "",
    name_ja: "",
    name_ko: "",
    name_id: "",
    name_ar: "",
    description_en: "",
    description_th: "",
    description_es: "",
    description_ja: "",
    description_ko: "",
    description_id: "",
    description_ar: "",
  });

  // Modal states
  const [isAddCategoryModalOpen, setIsAddCategoryModalOpen] = useState(false);
  const [isEditCategoryModalOpen, setIsEditCategoryModalOpen] = useState(false);
  const [isAddSubCategoryModalOpen, setIsAddSubCategoryModalOpen] =
    useState(false);
  const [isEditSubCategoryModalOpen, setIsEditSubCategoryModalOpen] =
    useState(false);
  const [isViewSubCategoriesModalOpen, setIsViewSubCategoriesModalOpen] =
    useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [editingSubCategory, setEditingSubCategory] =
    useState<SubCategory | null>(null);
  const [selectedCategoryForSub, setSelectedCategoryForSub] =
    useState<Category | null>(null);
  const [viewingCategory, setViewingCategory] = useState<Category | null>(null);
  const [viewSubCategories, setViewSubCategories] = useState<SubCategory[]>([]);

  // Delete modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<
    Category | SubCategory | null
  >(null);
  const [isSubCategoryDelete, setIsSubCategoryDelete] = useState(false);

  // Edit states
  const [editData, setEditData] = useState<Partial<Category> | null>(null);
  const [editSubCategoryData, setEditSubCategoryData] =
    useState<Partial<SubCategory> | null>(null);

  // Language tab states - SEPARATE for each context
  const [newCategoryLangTab, setNewCategoryLangTab] = useState<
    "en" | "th" | "es" | "ja" | "ko" | "id" | "ar"
  >("en");
  const [editCategoryLangTab, setEditCategoryLangTab] = useState<
    "en" | "th" | "es" | "ja" | "ko" | "id" | "ar"
  >("en");
  const [newSubCategoryLangTab, setNewSubCategoryLangTab] = useState<
    "en" | "th" | "es" | "ja" | "ko" | "id" | "ar"
  >("en");
  const [editSubCategoryLangTab, setEditSubCategoryLangTab] = useState<
    "en" | "th" | "es" | "ja" | "ko" | "id" | "ar"
  >("en");

  const sortedCategories = useMemo(() => {
    return [...categories].sort(
      (a, b) =>
        new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
    );
  }, [categories]);

  // Filtered categories based on selected filter
  const filteredCategories = useMemo(() => {
    if (selectedFilterCategory === "all") {
      return sortedCategories;
    }
    return sortedCategories.filter(
      (category) => category.id === selectedFilterCategory,
    );
  }, [sortedCategories, selectedFilterCategory]);

  // Get filtered subcategories based on subcategory filter
  const getFilteredSubCategories = useCallback(
    (categoryId: string) => {
      const categorySubCategories = subCategories.filter(
        (sc) => sc.categoryId === categoryId,
      );

      if (selectedFilterSubCategory === "all") {
        return categorySubCategories;
      }

      return categorySubCategories.filter(
        (sc) => sc.id === selectedFilterSubCategory,
      );
    },
    [subCategories, selectedFilterSubCategory],
  );

  // Get all subcategories for the dropdown (filtered by selected category)
  const filteredSubCategoriesForDropdown = useMemo(() => {
    if (selectedFilterCategory === "all") {
      return subCategories;
    }
    return subCategories.filter(
      (sc) => sc.categoryId === selectedFilterCategory,
    );
  }, [subCategories, selectedFilterCategory]);

  const handleAddCategory = useCallback(async () => {
    if (!newCategory.name_en.trim()) {
      toast.error("English category name is required");
      return;
    }

    try {
      const categoryData = {
        ...newCategory,
        name: newCategory.name_en,
        description: newCategory.description_en,
      };

      await addCategory(categoryData as any);
      setNewCategory({
        name: "",
        description: "",
        icon: "moon-star",
        color: "#6366f1",
        isActive: true,
        prompt: "",
        freeUserPrompt: "",
        name_en: "",
        name_th: "",
        name_es: "",
        name_ja: "",
        name_ko: "",
        name_id: "",
        name_ar: "",
        description_en: "",
        description_th: "",
        description_es: "",
        description_ja: "",
        description_ko: "",
        description_id: "",
        description_ar: "",
      });
      setNewCategoryLangTab("en");
      setIsAddCategoryModalOpen(false);
      toast.success("Category added successfully");
    } catch (e) {
      toast.error("Failed to add category");
    }
  }, [newCategory, addCategory]);

  const handleEditCategory = useCallback(async () => {
    if (!editData || !editingCategory) return;

    if (!String(editData.name_en || "").trim()) {
      toast.error("English category name is required");
      return;
    }

    try {
      const dataToUpdate = {
        ...editData,
        name: editData.name_en,
        description: editData.description_en,
      };

      await updateCategory(editingCategory.id, dataToUpdate);
      setIsEditCategoryModalOpen(false);
      setEditingCategory(null);
      setEditData(null);
      setEditCategoryLangTab("en");
      toast.success("Category updated successfully");
    } catch {
      toast.error("Failed to update category");
    }
  }, [editData, editingCategory, updateCategory]);

  const handleAddSubCategory = useCallback(async () => {
    if (!newSubCategory.name_en.trim()) {
      toast.error("English subcategory name is required");
      return;
    }

    if (!selectedCategoryForSub) {
      toast.error("No category selected");
      return;
    }

    try {
      const dataToAdd = {
        ...newSubCategory,
        name: newSubCategory.name_en,
        description: newSubCategory.description_en,
        categoryId: selectedCategoryForSub.id,
      };

      await addSubCategory(dataToAdd);

      setNewSubCategory({
        name: "",
        description: "",
        icon: "message-square",
        isActive: true,
        prompt: "",
        freeUserPrompt: "",
        categoryId: "",
        name_en: "",
        name_th: "",
        name_es: "",
        name_ja: "",
        name_ko: "",
        name_id: "",
        name_ar: "",
        description_en: "",
        description_th: "",
        description_es: "",
        description_ja: "",
        description_ko: "",
        description_id: "",
        description_ar: "",
      });

      setNewSubCategoryLangTab("en");
      setIsAddSubCategoryModalOpen(false);
      setSelectedCategoryForSub(null);

      // Refresh view subcategories if viewing this category
      if (viewingCategory?.id === selectedCategoryForSub.id) {
        const updatedSubs = await getSubCategoriesByCategory(
          selectedCategoryForSub.id,
        );
        setViewSubCategories(updatedSubs || []);
      }

      toast.success("SubCategory added successfully");
    } catch (e) {
      console.error("Failed to add subcategory:", e);
      toast.error("Failed to add subcategory");
    }
  }, [
    newSubCategory,
    selectedCategoryForSub,
    addSubCategory,
    getSubCategoriesByCategory,
    viewingCategory,
  ]);

  const handleEditSubCategory = useCallback(async () => {
    if (!editSubCategoryData || !editingSubCategory) return;

    if (!String(editSubCategoryData.name_en || "").trim()) {
      toast.error("English subcategory name is required");
      return;
    }

    try {
      const dataToUpdate = {
        ...editSubCategoryData,
        name: editSubCategoryData.name_en,
        description: editSubCategoryData.description_en,
      };

      await updateSubCategory(editingSubCategory.id, dataToUpdate);
      setIsEditSubCategoryModalOpen(false);
      setEditingSubCategory(null);
      setEditSubCategoryData(null);
      setEditSubCategoryLangTab("en");

      // Refresh view subcategories if viewing this category
      if (viewingCategory) {
        const updatedSubs = await getSubCategoriesByCategory(
          viewingCategory.id,
        );
        setViewSubCategories(updatedSubs || []);
      }

      toast.success("SubCategory updated successfully");
    } catch {
      toast.error("Failed to update subcategory");
    }
  }, [
    editSubCategoryData,
    editingSubCategory,
    updateSubCategory,
    getSubCategoriesByCategory,
    viewingCategory,
  ]);

  const handleDeleteClick = useCallback(
    (item: Category | SubCategory, isSubCategory: boolean = false) => {
      setCategoryToDelete(item);
      setIsSubCategoryDelete(isSubCategory);
      setDeleteModalOpen(true);
    },
    [],
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!categoryToDelete) return;

    try {
      if (isSubCategoryDelete) {
        await deleteSubCategory(categoryToDelete.id);
        // Refresh view subcategories if viewing this category
        if (viewingCategory) {
          const updatedSubs = await getSubCategoriesByCategory(
            viewingCategory.id,
          );
          setViewSubCategories(updatedSubs || []);
        }
      } else {
        await deleteCategory(categoryToDelete.id);
        // Close view modal if the deleted category was being viewed
        if (viewingCategory?.id === categoryToDelete.id) {
          setIsViewSubCategoriesModalOpen(false);
          setViewingCategory(null);
          setViewSubCategories([]);
        }
      }
      toast.success(
        `${isSubCategoryDelete ? "SubCategory" : "Category"} deleted successfully`,
      );
    } catch {
      toast.error(
        `Failed to delete ${isSubCategoryDelete ? "subcategory" : "category"}`,
      );
    } finally {
      setDeleteModalOpen(false);
      setCategoryToDelete(null);
      setIsSubCategoryDelete(false);
    }
  }, [
    categoryToDelete,
    isSubCategoryDelete,
    deleteSubCategory,
    deleteCategory,
    viewingCategory,
    getSubCategoriesByCategory,
  ]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteModalOpen(false);
    setCategoryToDelete(null);
    setIsSubCategoryDelete(false);
  }, []);

  const openEditModal = useCallback((category: Category) => {
    setEditingCategory(category);
    setEditData({
      ...category,
      name_en: category.name_en || category.name,
      name_th: category.name_th || "",
      name_es: category.name_es || "",
      name_ja: category.name_ja || "",
      name_ko: category.name_ko || "",
      name_id: category.name_id || "",
      name_ar: category.name_ar || "",
      description_en: category.description_en || category.description,
      description_th: category.description_th || "",
      description_es: category.description_es || "",
      description_ja: category.description_ja || "",
      description_ko: category.description_ko || "",
      description_id: category.description_id || "",
      description_ar: category.description_ar || "",
      freeUserPrompt: category.freeUserPrompt || "",
    });
    setEditCategoryLangTab("en");
    setIsEditCategoryModalOpen(true);
  }, []);

  const openAddSubCategoryModal = useCallback((category: Category) => {
    setSelectedCategoryForSub(category);
    setNewSubCategory({
      name: "",
      description: "",
      icon: "message-square",
      isActive: true,
      prompt: "",
      freeUserPrompt: "",
      categoryId: category.id,
      name_en: "",
      name_th: "",
      name_es: "",
      name_ja: "",
      name_ko: "",
      name_id: "",
      name_ar: "",
      description_en: "",
      description_th: "",
      description_es: "",
      description_ja: "",
      description_ko: "",
      description_id: "",
      description_ar: "",
    });
    setNewSubCategoryLangTab("en");
    setIsAddSubCategoryModalOpen(true);
  }, []);

  const openEditSubCategoryModal = useCallback((subCategory: SubCategory) => {
    setEditingSubCategory(subCategory);
    setEditSubCategoryData({
      ...subCategory,
      name_en: subCategory.name_en || subCategory.name,
      name_th: subCategory.name_th || "",
      name_es: subCategory.name_es || "",
      name_ja: subCategory.name_ja || "",
      name_ko: subCategory.name_ko || "",
      name_id: subCategory.name_id || "",
      name_ar: subCategory.name_ar || "",
      description_en: subCategory.description_en || subCategory.description,
      description_th: subCategory.description_th || "",
      description_es: subCategory.description_es || "",
      description_ja: subCategory.description_ja || "",
      description_ko: subCategory.description_ko || "",
      description_id: subCategory.description_id || "",
      description_ar: subCategory.description_ar || "",
      freeUserPrompt: subCategory.freeUserPrompt || "",
    });
    setEditSubCategoryLangTab("en");
    setIsEditSubCategoryModalOpen(true);
  }, []);

  const openViewSubCategoriesModal = useCallback(
    async (category: Category) => {
      setViewingCategory(category);
      try {
        const subs = await getSubCategoriesByCategory(category.id);
        setViewSubCategories(subs || []);
        setIsViewSubCategoriesModalOpen(true);
      } catch (error) {
        console.error("Failed to fetch subcategories:", error);
        toast.error("Failed to load subcategories");
      }
    },
    [getSubCategoriesByCategory],
  );

  const toggleVisibility = useCallback(
    async (id: string, isActive: boolean, isSubCategory: boolean = false) => {
      try {
        if (isSubCategory) {
          await updateSubCategory(id, { isActive: !isActive });
          // Refresh view subcategories if viewing this category
          if (viewingCategory) {
            const updatedSubs = await getSubCategoriesByCategory(
              viewingCategory.id,
            );
            setViewSubCategories(updatedSubs || []);
          }
        } else {
          await updateCategory(id, { isActive: !isActive });
        }
        toast.success(
          `${isSubCategory ? "SubCategory" : "Category"} ${!isActive ? "shown" : "hidden"}`,
        );
      } catch {
        toast.error(
          `Failed to update ${isSubCategory ? "subcategory" : "category"} visibility`,
        );
      }
    },
    [
      updateCategory,
      updateSubCategory,
      viewingCategory,
      getSubCategoriesByCategory,
    ],
  );

  const generateDefaultCategoryPrompt = useCallback((categoryName: string) => {
    return defaultCategoryPromptTemplate.replace(
      /{categoryName}/g,
      categoryName,
    );
  }, []);

  const generateDefaultFreeUserCategoryPrompt = useCallback(
    (categoryName: string) => {
      return defaultFreeUserPromptTemplate.replace(
        /{categoryName}/g,
        categoryName,
      );
    },
    [],
  );

  const generateDefaultSubCategoryPrompt = useCallback(
    (subcategoryName: string, categoryName: string) => {
      return defaultSubCategoryPromptTemplate
        .replace(/{subcategoryName}/g, subcategoryName)
        .replace(/{categoryName}/g, categoryName);
    },
    [],
  );

  const generateDefaultFreeUserSubCategoryPrompt = useCallback(
    (subcategoryName: string, categoryName: string) => {
      return defaultFreeUserSubCategoryPromptTemplate
        .replace(/{subcategoryName}/g, subcategoryName)
        .replace(/{categoryName}/g, categoryName);
    },
    [],
  );

  const applyDefaultCategoryPrompt = useCallback(() => {
    const name = newCategory.name_en || "";
    if (name.trim()) {
      setNewCategory((prev) => ({
        ...prev,
        prompt: generateDefaultCategoryPrompt(name),
      }));
      toast.success("Default category prompt applied");
    } else {
      toast.error("Please enter an English category name first");
    }
  }, [newCategory.name_en, generateDefaultCategoryPrompt]);

  const applyDefaultFreeUserCategoryPrompt = useCallback(() => {
    const name = newCategory.name_en || "";
    if (name.trim()) {
      setNewCategory((prev) => ({
        ...prev,
        freeUserPrompt: generateDefaultFreeUserCategoryPrompt(name),
      }));
      toast.success("Default free user prompt applied");
    } else {
      toast.error("Please enter an English category name first");
    }
  }, [newCategory.name_en, generateDefaultFreeUserCategoryPrompt]);

  const applyDefaultCategoryPromptEdit = useCallback(() => {
    if (editData?.name_en) {
      setEditData((prev) => ({
        ...(prev || {}),
        prompt: generateDefaultCategoryPrompt(editData.name_en || ""),
      }));
      toast.success("Default category prompt applied");
    }
  }, [editData?.name_en, generateDefaultCategoryPrompt]);

  const applyDefaultFreeUserCategoryPromptEdit = useCallback(() => {
    if (editData?.name_en) {
      setEditData((prev) => ({
        ...(prev || {}),
        freeUserPrompt: generateDefaultFreeUserCategoryPrompt(
          editData.name_en || "",
        ),
      }));
      toast.success("Default free user prompt applied");
    }
  }, [editData?.name_en, generateDefaultFreeUserCategoryPrompt]);

  const applyDefaultSubCategoryPrompt = useCallback(() => {
    const name = newSubCategory.name_en || "";
    if (name.trim() && selectedCategoryForSub) {
      setNewSubCategory((prev) => ({
        ...prev,
        prompt: generateDefaultSubCategoryPrompt(
          name,
          selectedCategoryForSub.name,
        ),
      }));
      toast.success("Default subcategory prompt applied");
    } else if (!name.trim()) {
      toast.error("Please enter an English subcategory name first");
    } else if (!selectedCategoryForSub) {
      toast.error("No category selected");
    }
  }, [
    newSubCategory.name_en,
    selectedCategoryForSub,
    generateDefaultSubCategoryPrompt,
  ]);

  const applyDefaultFreeUserSubCategoryPrompt = useCallback(() => {
    const name = newSubCategory.name_en || "";
    if (name.trim() && selectedCategoryForSub) {
      setNewSubCategory((prev) => ({
        ...prev,
        freeUserPrompt: generateDefaultFreeUserSubCategoryPrompt(
          name,
          selectedCategoryForSub.name,
        ),
      }));
      toast.success("Default free user subcategory prompt applied");
    } else if (!name.trim()) {
      toast.error("Please enter an English subcategory name first");
    } else if (!selectedCategoryForSub) {
      toast.error("No category selected");
    }
  }, [
    newSubCategory.name_en,
    selectedCategoryForSub,
    generateDefaultFreeUserSubCategoryPrompt,
  ]);

  const applyDefaultSubCategoryPromptEdit = useCallback(() => {
    if (editSubCategoryData?.name_en && editingSubCategory) {
      const category = categories.find(
        (c) => c.id === editingSubCategory.categoryId,
      );
      if (category) {
        setEditSubCategoryData((prev) => ({
          ...(prev || {}),
          prompt: generateDefaultSubCategoryPrompt(
            editSubCategoryData.name_en || "",
            category.name,
          ),
        }));
        toast.success("Default subcategory prompt applied");
      }
    }
  }, [
    editSubCategoryData?.name_en,
    editingSubCategory,
    categories,
    generateDefaultSubCategoryPrompt,
  ]);

  const applyDefaultFreeUserSubCategoryPromptEdit = useCallback(() => {
    if (editSubCategoryData?.name_en && editingSubCategory) {
      const category = categories.find(
        (c) => c.id === editingSubCategory.categoryId,
      );
      if (category) {
        setEditSubCategoryData((prev) => ({
          ...(prev || {}),
          freeUserPrompt: generateDefaultFreeUserSubCategoryPrompt(
            editSubCategoryData.name_en || "",
            category.name,
          ),
        }));
        toast.success("Default free user subcategory prompt applied");
      }
    }
  }, [
    editSubCategoryData?.name_en,
    editingSubCategory,
    categories,
    generateDefaultFreeUserSubCategoryPrompt,
  ]);

  const LanguageTab = useCallback(
    ({
      activeTab,
      onTabChange,
      lang,
      label,
    }: {
      activeTab: "en" | "th" | "es" | "ja" | "ko" | "id" | "ar";
      onTabChange: (tab: "en" | "th" | "es" | "ja" | "ko" | "id" | "ar") => void;
      lang: "en" | "th" | "es" | "ja" | "ko" | "id" | "ar";
      label: string;
    }) => (
      <button
        type="button"
        onClick={() => onTabChange(lang)}
        className={`flex-1 py-2 px-3 text-sm font-medium rounded-lg transition-all duration-200 ${
          activeTab === lang
            ? "bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-md"
            : "bg-gray-100 text-gray-700 hover:bg-gray-200"
        }`}
      >
        {label}
      </button>
    ),
    [],
  );

  const getCategoryGradient = useCallback((color: string) => {
    return `linear-gradient(135deg, ${color}20, ${color}05)`;
  }, []);

  // Memoized Category Modal
  const CategoryModalComponent = useCallback(
    ({
      isOpen,
      onClose,
      title,
      categoryData,
      setCategoryData,
      langTab,
      setLangTab,
      onSave,
      isEdit = false,
    }: {
      isOpen: boolean;
      onClose: () => void;
      title: string;
      categoryData: any;
      setCategoryData: (data: any) => void;
      langTab: "en" | "th" | "es" | "ja" | "ko" | "id" | "ar";
      setLangTab: (tab: "en" | "th" | "es" | "ja" | "ko" | "id" | "ar") => void;
      onSave: () => void;
      isEdit?: boolean;
    }) => {
      if (!isOpen) return null;

      const applyPrompt = isEdit
        ? applyDefaultCategoryPromptEdit
        : applyDefaultCategoryPrompt;

      const applyFreeUserPrompt = isEdit
        ? applyDefaultFreeUserCategoryPromptEdit
        : applyDefaultFreeUserCategoryPrompt;

      return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto hide-scrollbar animate-in zoom-in-95 duration-200">
            {/* Modal Header */}
            <div className="sticky top-0 z-20 bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-white">{title}</h3>
                <button
                  onClick={onClose}
                  className="text-white/80 hover:text-white transition p-1 rounded-lg hover:bg-white/10"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className="text-indigo-100 text-sm mt-1">
                {isEdit
                  ? "Update category details"
                  : "Create a new category for your platform"}
              </p>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-5">
              {/* Language Tabs */}
              <div className="flex gap-2">
                <LanguageTab
                  activeTab={langTab}
                  onTabChange={setLangTab}
                  lang="en"
                  label="🇬🇧 English"
                />
                <LanguageTab
                  activeTab={langTab}
                  onTabChange={setLangTab}
                  lang="th"
                  label="🇹🇭 Thai"
                />
                <LanguageTab
                  activeTab={langTab}
                  onTabChange={setLangTab}
                  lang="es"
                  label="🇪🇸 Spanish"
                />
                <LanguageTab
                  activeTab={langTab}
                  onTabChange={setLangTab}
                  lang="ja"
                  label="🇯🇵 Japanese"
                />
                <LanguageTab
                  activeTab={langTab}
                  onTabChange={setLangTab}
                  lang="ko"
                  label="🇰🇷 Korean"
                />
                <LanguageTab
                  activeTab={langTab}
                  onTabChange={setLangTab}
                  lang="id"
                  label="🇮🇩 Indonesian"
                />
                <LanguageTab
                  activeTab={langTab}
                  onTabChange={setLangTab}
                  lang="ar"
                  label="🇸🇦 Arabic"
                />
              </div>

              {/* Name and Description Fields */}
              <div className="space-y-4">
                {langTab === "en" && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Name (English) <span className="text-red-500">*</span>
                      </label>
                      <input
                        value={categoryData.name_en || ""}
                        onChange={(e) =>
                          setCategoryData({
                            ...categoryData,
                            name_en: e.target.value,
                          })
                        }
                        className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all"
                        placeholder="e.g., Mental Health, Relationships, Career"
                        autoFocus
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description (English)
                      </label>
                      <input
                        value={categoryData.description_en || ""}
                        onChange={(e) =>
                          setCategoryData({
                            ...categoryData,
                            description_en: e.target.value,
                          })
                        }
                        className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all"
                        placeholder="Brief description of this category"
                      />
                    </div>
                  </>
                )}

                {langTab === "th" && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        ชื่อ (Thai)
                      </label>
                      <input
                        value={categoryData.name_th || ""}
                        onChange={(e) =>
                          setCategoryData({
                            ...categoryData,
                            name_th: e.target.value,
                          })
                        }
                        className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all"
                        placeholder="ชื่อหมวดหมู่"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        คำอธิบาย (Thai)
                      </label>
                      <input
                        value={categoryData.description_th || ""}
                        onChange={(e) =>
                          setCategoryData({
                            ...categoryData,
                            description_th: e.target.value,
                          })
                        }
                        className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all"
                        placeholder="คำอธิบายภาษาไทย"
                      />
                    </div>
                  </>
                )}

                {langTab === "es" && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Nombre (Spanish)
                      </label>
                      <input
                        value={categoryData.name_es || ""}
                        onChange={(e) =>
                          setCategoryData({
                            ...categoryData,
                            name_es: e.target.value,
                          })
                        }
                        className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all"
                        placeholder="Nombre de la categoría"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Descripción (Spanish)
                      </label>
                      <input
                        value={categoryData.description_es || ""}
                        onChange={(e) =>
                          setCategoryData({
                            ...categoryData,
                            description_es: e.target.value,
                          })
                        }
                        className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all"
                        placeholder="Descripción en español"
                      />
                    </div>
                  </>
                )}

                {langTab === "ja" && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        名前 (Japanese)
                      </label>
                      <input
                        value={categoryData.name_ja || ""}
                        onChange={(e) =>
                          setCategoryData({
                            ...categoryData,
                            name_ja: e.target.value,
                          })
                        }
                        className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all"
                        placeholder="カテゴリ名"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        説明 (Japanese)
                      </label>
                      <input
                        value={categoryData.description_ja || ""}
                        onChange={(e) =>
                          setCategoryData({
                            ...categoryData,
                            description_ja: e.target.value,
                          })
                        }
                        className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all"
                        placeholder="カテゴリの説明"
                      />
                    </div>
                  </>
                )}

                {langTab === "ko" && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        이름 (Korean)
                      </label>
                      <input
                        value={categoryData.name_ko || ""}
                        onChange={(e) =>
                          setCategoryData({
                            ...categoryData,
                            name_ko: e.target.value,
                          })
                        }
                        className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all"
                        placeholder="카테고리 이름"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        설명 (Korean)
                      </label>
                      <input
                        value={categoryData.description_ko || ""}
                        onChange={(e) =>
                          setCategoryData({
                            ...categoryData,
                            description_ko: e.target.value,
                          })
                        }
                        className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all"
                        placeholder="카테고리 설명"
                      />
                    </div>
                  </>
                )}
                {langTab === "id" && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Nama (Indonesian)
                      </label>
                      <input
                        value={categoryData.name_id || ""}
                        onChange={(e) =>
                          setCategoryData({
                            ...categoryData,
                            name_id: e.target.value,
                          })
                        }
                        className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all"
                        placeholder="Nama kategori"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Deskripsi (Indonesian)
                      </label>
                      <input
                        value={categoryData.description_id || ""}
                        onChange={(e) =>
                          setCategoryData({
                            ...categoryData,
                            description_id: e.target.value,
                          })
                        }
                        className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all"
                        placeholder="Deskripsi kategori"
                      />
                    </div>
                  </>
                )}
                {langTab === "ar" && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        الاسم (Arabic)
                      </label>
                      <input
                        value={categoryData.name_ar || ""}
                        onChange={(e) =>
                          setCategoryData({
                            ...categoryData,
                            name_ar: e.target.value,
                          })
                        }
                        className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all"
                        placeholder="اسم الفئة"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        الوصف (Arabic)
                      </label>
                      <input
                        value={categoryData.description_ar || ""}
                        onChange={(e) =>
                          setCategoryData({
                            ...categoryData,
                            description_ar: e.target.value,
                          })
                        }
                        className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all"
                        placeholder="وصف الفئة"
                      />
                    </div>
                  </>
                )}
              </div>

              {/* Icon Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Icon
                </label>
                <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto hide-scrollbar p-2 border border-gray-200 rounded-xl">
                  {iconOptions.map((iconKey) => {
                    const Icon = getCategoryIcon(iconKey);
                    const isSelected = categoryData.icon === iconKey;
                    return (
                      <button
                        key={iconKey}
                        type="button"
                        onClick={() =>
                          setCategoryData({ ...categoryData, icon: iconKey })
                        }
                        className={`h-10 w-10 rounded-lg border flex items-center justify-center transition-all duration-200 ${
                          isSelected
                            ? "border-indigo-600 bg-indigo-600 text-white shadow-md scale-110"
                            : "border-gray-200 hover:border-indigo-300 hover:scale-105"
                        }`}
                        title={iconKey}
                      >
                        <Icon size={18} />
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Prompt Field */}
              <div>
                <div className="flex items-start justify-between mb-1">
                  <label className="block text-sm font-medium text-gray-700">
                    AI Prompt Template (Premium)
                  </label>
                  <button
                    type="button"
                    onClick={applyPrompt}
                    className="text-xs text-indigo-600 hover:text-indigo-800 font-medium flex items-center gap-1"
                  >
                    <Zap className="w-3 h-3" />
                    Use Default Template
                  </button>
                </div>
                <textarea
                  value={categoryData.prompt || ""}
                  onChange={(e) =>
                    setCategoryData({ ...categoryData, prompt: e.target.value })
                  }
                  rows={5}
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all resize-y"
                  placeholder="Custom AI prompt for premium users. Leave empty to use default system prompt."
                />
                <p className="text-xs text-gray-500 mt-1">
                  This prompt defines how the AI behaves for premium
                  conversations in this category.
                </p>
              </div>

              {/* Free User Prompt Field */}
              <div>
                <div className="flex items-start justify-between mb-1">
                  <label className="block text-sm font-medium text-gray-700">
                    Free User Prompt
                  </label>
                  <button
                    type="button"
                    onClick={applyFreeUserPrompt}
                    className="text-xs text-indigo-600 hover:text-indigo-800 font-medium flex items-center gap-1"
                  >
                    <Users className="w-3 h-3" />
                    Use Default Template
                  </button>
                </div>
                <textarea
                  value={categoryData.freeUserPrompt || ""}
                  onChange={(e) =>
                    setCategoryData({
                      ...categoryData,
                      freeUserPrompt: e.target.value,
                    })
                  }
                  rows={4}
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all resize-y"
                  placeholder="Custom AI prompt for free users. Leave empty to use default free user prompt."
                />
                <p className="text-xs text-gray-500 mt-1">
                  This prompt defines how the AI behaves for free users. It can
                  be more concise and include subtle upgrade suggestions.
                </p>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="sticky bottom-0 bg-gray-50 px-6 py-4 rounded-b-2xl border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 rounded-xl border border-gray-300 text-gray-700 font-medium hover:bg-gray-100 transition-all"
              >
                Cancel
              </button>
              <button
                onClick={onSave}
                className="px-4 py-2 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-medium hover:shadow-lg transition-all flex items-center gap-2"
              >
                {isEdit ? (
                  <Check className="w-4 h-4" />
                ) : (
                  <Plus className="w-4 h-4" />
                )}
                {isEdit ? "Save Changes" : "Create Category"}
              </button>
            </div>
          </div>
        </div>
      );
    },
    [
      LanguageTab,
      applyDefaultCategoryPrompt,
      applyDefaultCategoryPromptEdit,
      applyDefaultFreeUserCategoryPrompt,
      applyDefaultFreeUserCategoryPromptEdit,
      getCategoryIcon,
    ],
  );

  // Memoized SubCategory Modal
  const SubCategoryModalComponent = useCallback(
    ({
      isOpen,
      onClose,
      title,
      subCategoryData,
      setSubCategoryData,
      langTab,
      setLangTab,
      onSave,
      isEdit = false,
      categoryName,
    }: {
      isOpen: boolean;
      onClose: () => void;
      title: string;
      subCategoryData: any;
      setSubCategoryData: (data: any) => void;
      langTab: "en" | "th" | "es" | "ja" | "ko" | "id" | "ar";
      setLangTab: (tab: "en" | "th" | "es" | "ja" | "ko" | "id" | "ar") => void;
      onSave: () => void;
      isEdit?: boolean;
      categoryName?: string;
    }) => {
      if (!isOpen) return null;

      const applyPrompt = isEdit
        ? applyDefaultSubCategoryPromptEdit
        : applyDefaultSubCategoryPrompt;

      const applyFreeUserPrompt = isEdit
        ? applyDefaultFreeUserSubCategoryPromptEdit
        : applyDefaultFreeUserSubCategoryPrompt;

      return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto hide-scrollbar animate-in zoom-in-95 duration-200">
            {/* Modal Header */}
            <div className="sticky top-0 z-20 bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-white">{title}</h3>
                <button
                  onClick={onClose}
                  className="text-white/80 hover:text-white transition p-1 rounded-lg hover:bg-white/10"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className="text-indigo-100 text-sm mt-1">
                {isEdit
                  ? "Update subcategory details"
                  : `Create a new subcategory for "${categoryName || "category"}"`}
              </p>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-5">
              {/* Language Tabs */}
              <div className="flex gap-2">
                <LanguageTab
                  activeTab={langTab}
                  onTabChange={setLangTab}
                  lang="en"
                  label="🇬🇧 English"
                />
                <LanguageTab
                  activeTab={langTab}
                  onTabChange={setLangTab}
                  lang="th"
                  label="🇹🇭 Thai"
                />
                <LanguageTab
                  activeTab={langTab}
                  onTabChange={setLangTab}
                  lang="es"
                  label="🇪🇸 Spanish"
                />
                <LanguageTab
                  activeTab={langTab}
                  onTabChange={setLangTab}
                  lang="ja"
                  label="🇯🇵 Japanese"
                />
                <LanguageTab
                  activeTab={langTab}
                  onTabChange={setLangTab}
                  lang="ko"
                  label="🇰🇷 Korean"
                />
                <LanguageTab
                  activeTab={langTab}
                  onTabChange={setLangTab}
                  lang="id"
                  label="🇮🇩 Indonesian"
                />
                <LanguageTab
                  activeTab={langTab}
                  onTabChange={setLangTab}
                  lang="ar"
                  label="🇸🇦 Arabic"
                />
              </div>

              {/* Name and Description Fields */}
              <div className="space-y-4">
                {langTab === "en" && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Name (English) <span className="text-red-500">*</span>
                      </label>
                      <input
                        value={subCategoryData.name_en || ""}
                        onChange={(e) =>
                          setSubCategoryData({
                            ...subCategoryData,
                            name_en: e.target.value,
                          })
                        }
                        className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all"
                        placeholder="e.g., Anxiety Support, Career Guidance"
                        autoFocus
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description (English)
                      </label>
                      <input
                        value={subCategoryData.description_en || ""}
                        onChange={(e) =>
                          setSubCategoryData({
                            ...subCategoryData,
                            description_en: e.target.value,
                          })
                        }
                        className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all"
                        placeholder="Brief description of this subcategory"
                      />
                    </div>
                  </>
                )}

                {langTab === "th" && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        ชื่อ (Thai)
                      </label>
                      <input
                        value={subCategoryData.name_th || ""}
                        onChange={(e) =>
                          setSubCategoryData({
                            ...subCategoryData,
                            name_th: e.target.value,
                          })
                        }
                        className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all"
                        placeholder="ชื่อหมวดหมู่ย่อย"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        คำอธิบาย (Thai)
                      </label>
                      <input
                        value={subCategoryData.description_th || ""}
                        onChange={(e) =>
                          setSubCategoryData({
                            ...subCategoryData,
                            description_th: e.target.value,
                          })
                        }
                        className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all"
                        placeholder="คำอธิบายภาษาไทย"
                      />
                    </div>
                  </>
                )}

                {langTab === "es" && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Nombre (Spanish)
                      </label>
                      <input
                        value={subCategoryData.name_es || ""}
                        onChange={(e) =>
                          setSubCategoryData({
                            ...subCategoryData,
                            name_es: e.target.value,
                          })
                        }
                        className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all"
                        placeholder="Nombre de la subcategoría"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Descripción (Spanish)
                      </label>
                      <input
                        value={subCategoryData.description_es || ""}
                        onChange={(e) =>
                          setSubCategoryData({
                            ...subCategoryData,
                            description_es: e.target.value,
                          })
                        }
                        className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all"
                        placeholder="Descripción en español"
                      />
                    </div>
                  </>
                )}

                {langTab === "ja" && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        名前 (Japanese)
                      </label>
                      <input
                        value={subCategoryData.name_ja || ""}
                        onChange={(e) =>
                          setSubCategoryData({
                            ...subCategoryData,
                            name_ja: e.target.value,
                          })
                        }
                        className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all"
                        placeholder="サブカテゴリ名"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        説明 (Japanese)
                      </label>
                      <input
                        value={subCategoryData.description_ja || ""}
                        onChange={(e) =>
                          setSubCategoryData({
                            ...subCategoryData,
                            description_ja: e.target.value,
                          })
                        }
                        className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all"
                        placeholder="サブカテゴリの説明"
                      />
                    </div>
                  </>
                )}

                {langTab === "ko" && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        이름 (Korean)
                      </label>
                      <input
                        value={subCategoryData.name_ko || ""}
                        onChange={(e) =>
                          setSubCategoryData({
                            ...subCategoryData,
                            name_ko: e.target.value,
                          })
                        }
                        className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all"
                        placeholder="하위 카테고리 이름"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        설명 (Korean)
                      </label>
                      <input
                        value={subCategoryData.description_ko || ""}
                        onChange={(e) =>
                          setSubCategoryData({
                            ...subCategoryData,
                            description_ko: e.target.value,
                          })
                        }
                        className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all"
                        placeholder="하위 카테고리 설명"
                      />
                    </div>
                  </>
                )}
                {langTab === "id" && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Nama (Indonesian)
                      </label>
                      <input
                        value={subCategoryData.name_id || ""}
                        onChange={(e) =>
                          setSubCategoryData({
                            ...subCategoryData,
                            name_id: e.target.value,
                          })
                        }
                        className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all"
                        placeholder="Nama sub-kategori"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Deskripsi (Indonesian)
                      </label>
                      <input
                        value={subCategoryData.description_id || ""}
                        onChange={(e) =>
                          setSubCategoryData({
                            ...subCategoryData,
                            description_id: e.target.value,
                          })
                        }
                        className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all"
                        placeholder="Deskripsi sub-kategori"
                      />
                    </div>
                  </>
                )}
                {langTab === "ar" && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        الاسم (Arabic)
                      </label>
                      <input
                        value={subCategoryData.name_ar || ""}
                        onChange={(e) =>
                          setSubCategoryData({
                            ...subCategoryData,
                            name_ar: e.target.value,
                          })
                        }
                        className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all"
                        placeholder="اسم الفئة الفرعية"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        الوصف (Arabic)
                      </label>
                      <input
                        value={subCategoryData.description_ar || ""}
                        onChange={(e) =>
                          setSubCategoryData({
                            ...subCategoryData,
                            description_ar: e.target.value,
                          })
                        }
                        className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all"
                        placeholder="وصف الفئة الفرعية"
                      />
                    </div>
                  </>
                )}
              </div>

              {/* Icon Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Icon
                </label>
                <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto hide-scrollbar p-2 border border-gray-200 rounded-xl">
                  {iconOptions.map((iconKey) => {
                    const Icon = getCategoryIcon(iconKey);
                    const isSelected = subCategoryData.icon === iconKey;
                    return (
                      <button
                        key={iconKey}
                        type="button"
                        onClick={() =>
                          setSubCategoryData({
                            ...subCategoryData,
                            icon: iconKey,
                          })
                        }
                        className={`h-10 w-10 rounded-lg border flex items-center justify-center transition-all duration-200 ${
                          isSelected
                            ? "border-indigo-600 bg-indigo-600 text-white shadow-md scale-110"
                            : "border-gray-200 hover:border-indigo-300 hover:scale-105"
                        }`}
                        title={iconKey}
                      >
                        <Icon size={18} />
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Prompt Field */}
              <div>
                <div className="flex items-start justify-between mb-1">
                  <label className="block text-sm font-medium text-gray-700">
                    AI Prompt Template (Premium)
                  </label>
                  <button
                    type="button"
                    onClick={applyPrompt}
                    className="text-xs text-indigo-600 hover:text-indigo-800 font-medium flex items-center gap-1"
                  >
                    <Zap className="w-3 h-3" />
                    Use Default Template
                  </button>
                </div>
                <textarea
                  value={subCategoryData.prompt || ""}
                  onChange={(e) =>
                    setSubCategoryData({
                      ...subCategoryData,
                      prompt: e.target.value,
                    })
                  }
                  rows={5}
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all resize-y"
                  placeholder="Custom AI prompt for premium users. Leave empty to use category/default prompt."
                />
                <p className="text-xs text-gray-500 mt-1">
                  This prompt overrides the category prompt for premium
                  conversations in this subcategory.
                </p>
              </div>

              {/* Free User Prompt Field */}
              <div>
                <div className="flex items-start justify-between mb-1">
                  <label className="block text-sm font-medium text-gray-700">
                    Free User Prompt
                  </label>
                  <button
                    type="button"
                    onClick={applyFreeUserPrompt}
                    className="text-xs text-indigo-600 hover:text-indigo-800 font-medium flex items-center gap-1"
                  >
                    <Users className="w-3 h-3" />
                    Use Default Template
                  </button>
                </div>
                <textarea
                  value={subCategoryData.freeUserPrompt || ""}
                  onChange={(e) =>
                    setSubCategoryData({
                      ...subCategoryData,
                      freeUserPrompt: e.target.value,
                    })
                  }
                  rows={4}
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all resize-y"
                  placeholder="Custom AI prompt for free users. Leave empty to use category/default free user prompt."
                />
                <p className="text-xs text-gray-500 mt-1">
                  This prompt defines how the AI behaves for free users in this
                  subcategory.
                </p>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="sticky bottom-0 bg-gray-50 px-6 py-4 rounded-b-2xl border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 rounded-xl border border-gray-300 text-gray-700 font-medium hover:bg-gray-100 transition-all"
              >
                Cancel
              </button>
              <button
                onClick={onSave}
                className="px-4 py-2 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-medium hover:shadow-lg transition-all flex items-center gap-2"
              >
                {isEdit ? (
                  <Check className="w-4 h-4" />
                ) : (
                  <FolderPlus className="w-4 h-4" />
                )}
                {isEdit ? "Save Changes" : "Create SubCategory"}
              </button>
            </div>
          </div>
        </div>
      );
    },
    [
      LanguageTab,
      applyDefaultSubCategoryPrompt,
      applyDefaultSubCategoryPromptEdit,
      applyDefaultFreeUserSubCategoryPrompt,
      applyDefaultFreeUserSubCategoryPromptEdit,
      getCategoryIcon,
    ],
  );

  // View SubCategories Modal Component
  const ViewSubCategoriesModal = useCallback(() => {
    if (!isViewSubCategoriesModalOpen || !viewingCategory) return null;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
        <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto hide-scrollbar animate-in zoom-in-95 duration-200">
          {/* Modal Header */}
          <div className="sticky top-0 z-20 bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 rounded-t-2xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-white/20 flex items-center justify-center">
                  {(() => {
                    const Icon = getCategoryIcon(viewingCategory.icon);
                    return <Icon size={20} className="text-white" />;
                  })()}
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">
                    {viewingCategory.name}
                  </h3>
                  <p className="text-indigo-100 text-sm">
                    Subcategories ({viewSubCategories.length})
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  setIsViewSubCategoriesModalOpen(false);
                  setViewingCategory(null);
                  setViewSubCategories([]);
                }}
                className="text-white/80 hover:text-white transition p-1 rounded-lg hover:bg-white/10"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Modal Content */}
          <div className="p-6">
            {viewSubCategories.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Layers className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <p className="text-base">No subcategories found.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {viewSubCategories.map((subCategory) => (
                  <div
                    key={subCategory.id}
                    className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 flex-1">
                        <div className="h-10 w-10 rounded-lg border border-gray-200 flex items-center justify-center bg-gray-50">
                          {(() => {
                            const Icon = getCategoryIcon(subCategory.icon);
                            return <Icon size={18} className="text-gray-600" />;
                          })()}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-gray-900">
                              {subCategory.name}
                            </span>
                            <span
                              className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                subCategory.isActive
                                  ? "bg-green-100 text-green-700"
                                  : "bg-gray-100 text-gray-500"
                              }`}
                            >
                              {subCategory.isActive ? "Active" : "Hidden"}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 ml-4">
                        <button
                          onClick={() =>
                            toggleVisibility(
                              subCategory.id,
                              subCategory.isActive,
                              true,
                            )
                          }
                          className="h-9 w-9 rounded-lg border border-gray-200 hover:bg-gray-50 transition inline-flex items-center justify-center"
                          title={subCategory.isActive ? "Hide" : "Show"}
                        >
                          {subCategory.isActive ? (
                            <EyeOff className="w-4 h-4 text-gray-600" />
                          ) : (
                            <Eye className="w-4 h-4 text-gray-600" />
                          )}
                        </button>
                        <button
                          onClick={() => {
                            setIsViewSubCategoriesModalOpen(false);
                            openEditSubCategoryModal(subCategory);
                          }}
                          className="h-9 w-9 rounded-lg border border-gray-200 hover:bg-gray-50 transition inline-flex items-center justify-center"
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4 text-gray-600" />
                        </button>
                        <button
                          onClick={() => {
                            setIsViewSubCategoriesModalOpen(false);
                            handleDeleteClick(subCategory, true);
                          }}
                          className="h-9 w-9 rounded-lg border border-gray-200 hover:bg-red-50 transition inline-flex items-center justify-center"
                          title="Delete"
                        >
                          <Trash2Icon className="w-4 h-4 text-red-500" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Modal Footer */}
          <div className="sticky bottom-0 bg-gray-50 px-6 py-4 rounded-b-2xl border-t border-gray-200 flex justify-end gap-3">
            <button
              onClick={() => {
                setIsViewSubCategoriesModalOpen(false);
                setViewingCategory(null);
                setViewSubCategories([]);
              }}
              className="px-4 py-2 rounded-xl border border-gray-300 text-gray-700 font-medium hover:bg-gray-100 transition-all"
            >
              Close
            </button>
            <button
              onClick={() => {
                setIsViewSubCategoriesModalOpen(false);
                openAddSubCategoryModal(viewingCategory);
              }}
              className="px-4 py-2 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-medium hover:shadow-lg transition-all flex items-center gap-2"
            >
              <FolderPlus className="w-4 h-4" />
              Add Subcategory
            </button>
          </div>
        </div>
      </div>
    );
  }, [
    isViewSubCategoriesModalOpen,
    viewingCategory,
    viewSubCategories,
    openAddSubCategoryModal,
    openEditSubCategoryModal,
    handleDeleteClick,
    toggleVisibility,
    getCategoryIcon,
  ]);

  return (
    <div className="bg-white">
      {/* Header with Add Button and Filters */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between gap-4 mb-5">
          <h2 className="text-xl font-bold text-gray-900">
            Categories & Subcategories
          </h2>
          <button
            onClick={() => setIsAddCategoryModalOpen(true)}
            className="px-5 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg font-medium hover:shadow-lg transition-all flex items-center gap-2 text-sm"
          >
            <Plus className="w-4 h-4" />
            New Category
          </button>
        </div>

        {/* Compact Filters Row */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-400" />
            <span className="text-sm text-gray-600 font-medium">
              Filter by:
            </span>
          </div>

          {/* Category Filter */}
          <div className="relative">
            <select
              value={selectedFilterCategory}
              onChange={(e) => setSelectedFilterCategory(e.target.value)}
              className="appearance-none pl-9 pr-8 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 hover:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all cursor-pointer font-medium"
            >
              <option value="all">All Categories</option>
              {sortedCategories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
            <Tag className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <ChevronDown className="absolute right-2.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          </div>

          {/* Subcategory Filter */}
          <div className="relative">
            <select
              value={selectedFilterSubCategory}
              onChange={(e) => setSelectedFilterSubCategory(e.target.value)}
              className="appearance-none pl-9 pr-8 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 hover:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              disabled={filteredSubCategoriesForDropdown.length === 0}
            >
              <option value="all">
                {filteredSubCategoriesForDropdown.length === 0
                  ? "No subcategories"
                  : "All Subcategories"}
              </option>
              {filteredSubCategoriesForDropdown.map((subCategory) => (
                <option key={subCategory.id} value={subCategory.id}>
                  {subCategory.name}
                </option>
              ))}
            </select>
            <Layers className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <ChevronDown className="absolute right-2.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          </div>

          {/* Clear All Filters Button */}
          {(selectedFilterCategory !== "all" ||
            selectedFilterSubCategory !== "all") && (
            <button
              onClick={() => {
                setSelectedFilterCategory("all");
                setSelectedFilterSubCategory("all");
              }}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors font-medium"
            >
              <XCircle className="w-4 h-4" />
              Clear filters
            </button>
          )}
        </div>

        {/* Active Filters Badges */}
        {(selectedFilterCategory !== "all" ||
          selectedFilterSubCategory !== "all") && (
          <div className="flex items-center gap-2 flex-wrap mt-3 pt-3 border-t border-gray-100">
            {selectedFilterCategory !== "all" && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 text-indigo-700 rounded-lg text-sm font-medium">
                <Tag className="w-3.5 h-3.5" />
                Category:{" "}
                {categories.find((c) => c.id === selectedFilterCategory)?.name}
                <button
                  onClick={() => setSelectedFilterCategory("all")}
                  className="hover:text-indigo-900 ml-1"
                >
                  <XCircle className="w-3.5 h-3.5" />
                </button>
              </span>
            )}
            {selectedFilterSubCategory !== "all" && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-purple-50 text-purple-700 rounded-lg text-sm font-medium">
                <Layers className="w-3.5 h-3.5" />
                Subcategory:{" "}
                {
                  subCategories.find(
                    (sc) => sc.id === selectedFilterSubCategory,
                  )?.name
                }
                <button
                  onClick={() => setSelectedFilterSubCategory("all")}
                  className="hover:text-purple-900 ml-1"
                >
                  <XCircle className="w-3.5 h-3.5" />
                </button>
              </span>
            )}
          </div>
        )}
      </div>

      {/* Categories Table */}
      <div className="p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-indigo-600" />
            {selectedFilterSubCategory === "all"
              ? "Existing Categories"
              : "Filtered Subcategory"}
          </h3>
          <p className="text-sm text-gray-500">Sorted by recently updated</p>
        </div>

        {selectedFilterSubCategory !== "all" ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    className="px-6 w-[10px] py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Icon
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Subcategory Name
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Category
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {subCategories
                  .filter((sc) => sc.id === selectedFilterSubCategory)
                  .map((subCategory) => {
                    const parentCategory = categories.find(
                      (c) => c.id === subCategory.categoryId,
                    );
                    return (
                      <tr
                        key={subCategory.id}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="h-10 w-10 rounded-lg border border-gray-200 flex items-center justify-center shrink-0 bg-gray-50">
                            {(() => {
                              const Icon = getCategoryIcon(subCategory.icon);
                              return (
                                <Icon size={18} className="text-gray-600" />
                              );
                            })()}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-gray-900">
                              {subCategory.name}
                            </span>
                            <span
                              className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                subCategory.isActive
                                  ? "bg-green-100 text-green-700"
                                  : "bg-gray-100 text-gray-500"
                              }`}
                            >
                              {subCategory.isActive ? "Active" : "Hidden"}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-600">
                            {parentCategory?.name || "—"}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() =>
                                toggleVisibility(
                                  subCategory.id,
                                  subCategory.isActive,
                                  true,
                                )
                              }
                              className="h-9 w-9 rounded-lg border border-gray-200 hover:bg-gray-50 transition inline-flex items-center justify-center"
                              title={
                                subCategory.isActive
                                  ? "Hide subcategory"
                                  : "Show subcategory"
                              }
                            >
                              {subCategory.isActive ? (
                                <EyeOff className="w-4 h-4 text-gray-600" />
                              ) : (
                                <Eye className="w-4 h-4 text-gray-600" />
                              )}
                            </button>
                            <button
                              onClick={() =>
                                openEditSubCategoryModal(subCategory)
                              }
                              className="h-9 w-9 rounded-lg border border-gray-200 hover:bg-gray-50 transition inline-flex items-center justify-center"
                              title="Edit"
                            >
                              <Edit2 className="w-4 h-4 text-gray-600" />
                            </button>
                            <button
                              onClick={() =>
                                handleDeleteClick(subCategory, true)
                              }
                              className="h-9 w-9 rounded-lg border border-gray-200 hover:bg-red-50 transition inline-flex items-center justify-center"
                              title="Delete"
                            >
                              <Trash2Icon className="w-4 h-4 text-red-500" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        ) : filteredCategories.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <FolderPlus className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <p className="text-base">
              No categories found.{" "}
              {selectedFilterCategory !== "all" &&
                "Try selecting a different filter or "}
              click "New Category" to get started.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    className="px-6 w-[10px] py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Icon
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Category Name
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Subcategories
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredCategories.map((category) => {
                  const categorySubCategories = getFilteredSubCategories(
                    category.id,
                  );

                  // Skip this category if subcategory filter is active and no matching subcategories
                  if (
                    selectedFilterSubCategory !== "all" &&
                    categorySubCategories.length === 0
                  ) {
                    return null;
                  }

                  return (
                    <tr
                      key={category.id}
                      onClick={() => {
                        if (selectedFilterSubCategory === "all") {
                          openViewSubCategoriesModal(category);
                        }
                      }}
                      className={`transition-colors ${
                        selectedFilterSubCategory === "all"
                          ? "hover:bg-gray-50 cursor-pointer"
                          : "hover:bg-gray-50"
                      }`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div
                          className="h-10 w-10 rounded-lg border border-gray-200 flex items-center justify-center shrink-0"
                          style={{
                            background: getCategoryGradient(category.color),
                          }}
                        >
                          {(() => {
                            const Icon = getCategoryIcon(category.icon);
                            return (
                              <Icon
                                size={18}
                                style={{ color: category.color }}
                              />
                            );
                          })()}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openViewSubCategoriesModal(category);
                            }}
                            className="font-semibold text-gray-900 hover:text-indigo-600 transition-colors flex items-center gap-1"
                          >
                            {category.name}
                          </button>
                          <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 font-medium">
                            {categorySubCategories.length} subs
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-600">
                            {getSubCategorySummary(categorySubCategories)}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleVisibility(category.id, category.isActive);
                            }}
                            className="h-9 w-9 rounded-lg border border-gray-200 hover:bg-gray-50 transition inline-flex items-center justify-center"
                            title={
                              category.isActive
                                ? "Hide category"
                                : "Show category"
                            }
                          >
                            {category.isActive ? (
                              <EyeOff className="w-4 h-4 text-gray-600" />
                            ) : (
                              <Eye className="w-4 h-4 text-gray-600" />
                            )}
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openEditModal(category);
                            }}
                            className="h-9 w-9 rounded-lg border border-gray-200 hover:bg-gray-50 transition inline-flex items-center justify-center"
                            title="Edit"
                          >
                            <Edit2 className="w-4 h-4 text-gray-600" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteClick(category);
                            }}
                            className="h-9 w-9 rounded-lg border border-gray-200 hover:bg-red-50 transition inline-flex items-center justify-center"
                            title="Delete"
                          >
                            <Trash2Icon className="w-4 h-4 text-red-500" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add Category Modal */}
      <CategoryModalComponent
        isOpen={isAddCategoryModalOpen}
        onClose={() => setIsAddCategoryModalOpen(false)}
        title="Create New Category"
        categoryData={newCategory}
        setCategoryData={setNewCategory}
        langTab={newCategoryLangTab}
        setLangTab={setNewCategoryLangTab}
        onSave={handleAddCategory}
        isEdit={false}
      />

      {/* Edit Category Modal */}
      <CategoryModalComponent
        isOpen={isEditCategoryModalOpen}
        onClose={() => {
          setIsEditCategoryModalOpen(false);
          setEditingCategory(null);
          setEditData(null);
        }}
        title="Edit Category"
        categoryData={editData || {}}
        setCategoryData={setEditData}
        langTab={editCategoryLangTab}
        setLangTab={setEditCategoryLangTab}
        onSave={handleEditCategory}
        isEdit={true}
      />

      {/* Add SubCategory Modal */}
      <SubCategoryModalComponent
        isOpen={isAddSubCategoryModalOpen}
        onClose={() => {
          setIsAddSubCategoryModalOpen(false);
          setSelectedCategoryForSub(null);
        }}
        title="Create New SubCategory"
        subCategoryData={newSubCategory}
        setSubCategoryData={setNewSubCategory}
        langTab={newSubCategoryLangTab}
        setLangTab={setNewSubCategoryLangTab}
        onSave={handleAddSubCategory}
        isEdit={false}
        categoryName={selectedCategoryForSub?.name}
      />

      {/* Edit SubCategory Modal */}
      <SubCategoryModalComponent
        isOpen={isEditSubCategoryModalOpen}
        onClose={() => {
          setIsEditSubCategoryModalOpen(false);
          setEditingSubCategory(null);
          setEditSubCategoryData(null);
        }}
        title="Edit SubCategory"
        subCategoryData={editSubCategoryData || {}}
        setSubCategoryData={setEditSubCategoryData}
        langTab={editSubCategoryLangTab}
        setLangTab={setEditSubCategoryLangTab}
        onSave={handleEditSubCategory}
        isEdit={true}
        categoryName={
          categories.find((c) => c.id === editingSubCategory?.categoryId)?.name
        }
      />

      {/* View SubCategories Modal */}
      <ViewSubCategoriesModal />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={deleteModalOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title={`Delete ${isSubCategoryDelete ? "SubCategory" : "Category"}`}
        message={`Are you sure you want to delete this ${isSubCategoryDelete ? "subcategory" : "category"}?`}
        itemName={categoryToDelete?.name}
      />
    </div>
  );
};

export default CategoryManager;
